import RootLayout from "@/app/layouts/RootLayout";
import {
    AfgerondPage,
    BeheerPage,
    DossierDetailsPage,
    DossierLayout,
    ErrorPage,
    HomePage,
    LoginPage,
    ReleaseHighlightsPage,
    ProfilePage,
    ProtectedRoute,
    TestScenariosPage,
    UnauthorizedPage,
    VestigingenPage,
    VosOproepenPage,
    VosRegistratie,
    VossenPage,
} from "@/app/pages";
import { UserRole } from "@/features/auth";
import { BetrokkenTreinenDetails } from "@/features/betrokken-treinen";
import { EvaluatieDetails } from "@/features/evaluatie";
import { FaciliteitenDetails, LandelijkeFaciliteitenDetails } from "@/features/faciliteiten";
import { MedewerkersOpStationDetails } from "@/features/medewerkers-op-station";
import { TvvDetails } from "@/features/tvv";
import { VsmOpstartplanDetails } from "@/features/vsm-of-opstartplan/components/vsm-opstartplan-details";
import { createBrowserRouter, Outlet, RouteObject } from "react-router";

// Admin Routes - Protected with authentication
const adminRoutes: RouteObject[] = [
    {
        path: "beheer",
        element: <BeheerPage />,
    },
    {
        path: "scenarios",
        element: <TestScenariosPage />,
    },
    {
        path: "vos-oproepen",
        element: <VosOproepenPage />,
    },
    {
        path: "release-highlights",
        element: <ReleaseHighlightsPage />,
    },
];

// Beheer routes
const beheerRoutes: RouteObject[] = [
    {
        path: "vestigingen",
        element: <VestigingenPage />,
    },
    {
        path: "vossen",
        element: <VossenPage />,
    },
];

// Dossier child routes - shared between active and afgerond dossier views
const dossierChildRoutes: RouteObject[] = [
    {
        index: true,
        element: <DossierDetailsPage />,
    },
    {
        path: "vsm",
        element: (
            <ProtectedRoute
                requiredRoles={[
                    UserRole.REGIEVOERDER,
                    UserRole.SUPPORTCONSULTANT,
                    UserRole.ALLEENLEZEN,
                    UserRole.KLANTENSERVICE,
                    UserRole.TRANSVISION,
                    UserRole.DEWIT,
                ]}
            >
                <VsmOpstartplanDetails />
            </ProtectedRoute>
        ),
    },
    {
        path: "tvv",
        element: (
            <ProtectedRoute
                requiredRoles={[
                    UserRole.REGIEVOERDER,
                    UserRole.SUPPORTCONSULTANT,
                    UserRole.TRANSVISION,
                    UserRole.DEWIT,
                    UserRole.ALLEENLEZEN,
                    UserRole.KLANTENSERVICE,
                ]}
            >
                <TvvDetails />
            </ProtectedRoute>
        ),
    },
    {
        path: "faciliteiten",
        element: (
            <ProtectedRoute
                requiredRoles={[UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.KLANTENSERVICE, UserRole.ALLEENLEZEN]}
            >
                <FaciliteitenDetails />
            </ProtectedRoute>
        ),
    },
    {
        path: "landelijke-faciliteiten",
        element: (
            <ProtectedRoute
                requiredRoles={[UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.KLANTENSERVICE, UserRole.ALLEENLEZEN]}
            >
                <LandelijkeFaciliteitenDetails />
            </ProtectedRoute>
        ),
    },
    {
        path: "betrokken-treinen",
        element: (
            <ProtectedRoute
                requiredRoles={[
                    UserRole.REGIEVOERDER,
                    UserRole.ALLEENLEZEN,
                    UserRole.SUPPORTCONSULTANT,
                    UserRole.TRANSVISION,
                    UserRole.DEWIT,
                    UserRole.KLANTENSERVICE,
                ]}
            >
                <BetrokkenTreinenDetails />
            </ProtectedRoute>
        ),
    },
    {
        path: "medewerkers-op-station",
        element: (
            <ProtectedRoute requiredRoles={[UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT]}>
                <MedewerkersOpStationDetails />
            </ProtectedRoute>
        ),
    },
    {
        path: "evaluatie",
        element: (
            <ProtectedRoute requiredRoles={[UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.ALLEENLEZEN]}>
                <EvaluatieDetails />
            </ProtectedRoute>
        ),
    },
];

// Root Routes
export const rootRoutes: RouteObject[] = [
    {
        path: "/vos-registratie",
        element: <VosRegistratie />,
    },
    {
        path: "/",
        element: <RootLayout />,
        children: [
            {
                path: "unauthorized",
                element: <UnauthorizedPage />,
            },
            {
                element: <LoginPage />,
                children: [
                    {
                        path: "/",
                        element: (
                            <ProtectedRoute
                                requiredRoles={[
                                    UserRole.ADMIN,
                                    UserRole.REGIEVOERDER,
                                    UserRole.KLANTENSERVICE,
                                    UserRole.TRANSVISION,
                                    UserRole.DEWIT,
                                    UserRole.SUPPORTCONSULTANT,
                                    UserRole.VRIJWILLIGER,
                                    UserRole.ALLEENLEZEN,
                                ]}
                            >
                                <HomePage />
                            </ProtectedRoute>
                        ),
                        children: [
                            {
                                path: "dossier/:id",
                                element: <DossierLayout />,
                                children: dossierChildRoutes,
                            },
                        ],
                    },
                    {
                        path: "gepland",
                        element: (
                            <ProtectedRoute requiredRoles={[UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.ALLEENLEZEN]}>
                                <div className="flex h-full w-full flex-row items-center justify-center gap-4">
                                    <h1 className="text-xxxl font-bold tracking-tight">⏰ Coming soon </h1>
                                </div>
                            </ProtectedRoute>
                        ),
                        errorElement: <ErrorPage />,
                    },
                    {
                        path: "afgerond",
                        element: (
                            <ProtectedRoute
                                requiredRoles={[
                                    UserRole.REGIEVOERDER,
                                    UserRole.KLANTENSERVICE,
                                    UserRole.TRANSVISION,
                                    UserRole.DEWIT,
                                    UserRole.SUPPORTCONSULTANT,
                                    UserRole.VRIJWILLIGER,
                                    UserRole.ALLEENLEZEN,
                                ]}
                            >
                                <AfgerondPage />
                            </ProtectedRoute>
                        ),
                        children: [
                            {
                                path: "dossier/:id",
                                element: <DossierLayout />,
                                children: dossierChildRoutes,
                            },
                        ],
                        errorElement: <ErrorPage />,
                    },
                    {
                        path: "admin",
                        element: (
                            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                                <Outlet />
                            </ProtectedRoute>
                        ),
                        children: adminRoutes,
                        errorElement: <ErrorPage />,
                    },
                    {
                        path: "beheer",
                        element: (
                            <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.SUPPORTCONSULTANT]}>
                                <Outlet />
                            </ProtectedRoute>
                        ),
                        children: beheerRoutes,
                        errorElement: <ErrorPage />,
                    },
                    {
                        path: "profile",
                        element: <ProfilePage />,
                        errorElement: <ErrorPage />,
                    },
                ],
            },
        ],
    },
];

export const router = createBrowserRouter(rootRoutes);
